<script setup lang="ts">
import { onUnmounted } from 'vue';

import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';
import { MapComponent } from '@apps/main/new-desktop/modules/maps';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';

import type { Map, StaticMap } from '@libs/maps';

import { SearchByMovingMapToggle } from '../../../modules/searchByMovingMapToggle';
import { useHotelSelectionStore, useHotelSelectionMapStore } from '../stores';

import HotelMapCard from './HotelMapCard.vue';

const browserStore = useBrowserStore();
const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionMapStore = useHotelSelectionMapStore();
const statsigEventsStore = useStatsigEventsStore();
const mapStore = useMapStore();

onUnmounted(() => hotelSelectionMapStore.destroyMap());

function onHotelCardClicked(): void {
  statsigEventsStore.logHotelSelectionMapCardClicked();
  hotelSelectionMapStore.hotelCardClicked();
}

function onToggleSearchByMovingMap(): void {
  const newValue = !mapStore.searchByMovingMap;
  statsigEventsStore.logCoreSearchByMovingMapToggle(newValue, {
    source: CoreEventSource.HotelSelection,
  });
  hotelSelectionMapStore.toggleSearchByMovingMap();
}

function setMapIntegration(map: Map | StaticMap): void {
  hotelSelectionMapStore.setDynamicMapIntegration(map as Map);
}
</script>

<template>
  <div
    class="hotel-selection-map"
    :class="{ loading: !hotelSelectionMapStore.isMapCreated }"
  >
    <SearchByMovingMapToggle
      :is-loading="hotelSelectionStore.shouldDisplayLoader"
      :search-by-moving-map="mapStore.searchByMapMovement"
      class="search-by-moving-map"
      @toggle-search-by-moving-map="onToggleSearchByMovingMap"
    />

    <MapComponent
      :marker-formatters="hotelSelectionMapStore.mapProperties.markerFormatters"
      :cluster-marker-formatters="
        hotelSelectionMapStore.mapProperties.clusterMarkerFormatters
      "
      :cluster-options="hotelSelectionMapStore.mapProperties.clusterOptions"
      :markers="hotelSelectionMapStore.mapProperties.markers"
      :route="hotelSelectionMapStore.mapProperties.route"
      :show-all-markers="hotelSelectionMapStore.mapProperties.showAllMarkers"
      :start-rectangle="hotelSelectionMapStore.mapProperties.startRectangle"
      :gesture-handling="hotelSelectionMapStore.mapProperties.gestureHandling"
      :locale="hotelSelectionMapStore.mapProperties.locale"
      :map-controls="hotelSelectionMapStore.mapProperties.mapControls"
      @on-marker-clicked="hotelSelectionMapStore.markerClicked"
      @on-view-changed="hotelSelectionMapStore.mapViewChanged"
      @map-created="setMapIntegration"
      @on-map-type-id-changed="hotelSelectionMapStore.setMapTypeId"
    />

    <HotelMapCard
      v-if="
        hotelSelectionMapStore.highlightedHotel?.showHotelMapCard &&
        hotelSelectionMapStore.hotelMapCardData
      "
      :data="hotelSelectionMapStore.hotelMapCardData"
      :class="{ mobile: browserStore.isSmallerThan1200px }"
      class="hotel-map-card"
      @click="onHotelCardClicked"
      @hide="hotelSelectionMapStore.hideHotelCard"
    />
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-map {
  width: 100%;
  height: 100%;
  position: relative;

  .hotel-map-card {
    --margin-from-toggle-to-bottom: 24px;
    --toggle-height: 44px;

    position: absolute;
    bottom: var(--spacing-16);
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;

    &.mobile {
      bottom: calc(
        var(--margin-from-toggle-to-bottom) + var(--toggle-height) +
          var(--spacing-16)
      );
    }
  }

  .search-by-moving-map {
    position: absolute;
    top: var(--spacing-64);
    left: var(--spacing-12);
    z-index: 1;
  }

  // NOTE: Initial loading state for map
  &.loading::after {
    @include animated-background-secondary;
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
  }

  &::after {
    content: none;
  }
}
</style>

<style lang="scss">
.hotel-selection-map {
  .hotel-selection-accommodation-marker {
    .hotel-selection-map-marker-label {
      @include font-label-m-bold;
    }

    cursor: pointer;

    padding: 6px var(--spacing-8);
    border: 1px solid var(--color-neutral-40);
    border-radius: var(--border-radius-8);
    background: var(--color-white);
    color: var(--color-neutral-70);
    box-shadow: var(--shadow);
    @include font-label-l-bold;
    transition: all var(--transition-short);
    @include on-hover {
      background-color: var(--color-neutral-30);
      border: 1px solid var(--color-neutral-40);
    }

    &.preselected {
      padding: 6px var(--spacing-8) 6px var(--spacing-32);
      border: 2px solid var(--color-brand-violet);
      border-radius: var(--border-radius-8);
      background: var(--color-white)
        url('../../../../assets/icon/scss/map/check-primary.svg')
        var(--spacing-8) center no-repeat;
      color: var(--color-neutral-70);
      box-shadow: var(--shadow);
      @include font-label-l-bold;
      transition: all var(--transition-short);
      @include on-hover {
        background-color: var(--color-neutral-30);
        border: 2px solid var(--color-neutral-70);
      }
    }

    &.highlighted.preselected {
      padding: 6px var(--spacing-8) 6px var(--spacing-32);
      border: 2px solid var(--color-brand-violet);
      border-radius: var(--border-radius-8);
      background: var(--color-brand-violet)
        url('../../../../assets/icon/scss/map/check-white.svg') var(--spacing-8)
        center no-repeat;
      color: var(--color-white);
      box-shadow: var(--shadow);
      @include font-label-l-bold;
      transition: all var(--transition-short);
      @include on-hover {
        background-color: var(--color-neutral-70);
        border: 2px solid var(--color-neutral-70);
      }
    }

    &.highlighted {
      padding: 6px var(--spacing-8);
      border: 2px solid var(--color-brand-violet);
      border-radius: var(--border-radius-8);
      background: var(--color-brand-violet);
      color: var(--color-white);
      box-shadow: var(--shadow);
      @include font-label-l-bold;
      transition: all var(--transition-short);
      @include on-hover {
        background-color: var(--color-neutral-70);
        border: 2px solid var(--color-neutral-70);
      }
    }

    @include for-less-or-equal-than-1200-px {
      padding: 5px var(--spacing-16);

      .hearted,
      .hearted.selected,
      .hearted.preselected {
        background-size: auto 18px;
        padding-left: 34px;
      }
    }
  }
  .trip-planner-hotel-marker {
    padding: 6px var(--spacing-16);
    cursor: pointer;
    border-radius: var(--border-radius-12);
    border: 1px solid var(--color-neutral-50);
    background-color: var(--color-white);
    color: var(--color-neutral-70);
    filter: drop-shadow(0px 7px 14px rgba(0, 0, 0, 0.15));
    @include font-label-m-bold;
    @include on-hover {
      background-color: var(--color-neutral-30);
      z-index: 1;
    }

    &.selected,
    &.preselected {
      background-color: var(--color-neutral-70);
      color: var(--color-white);
      @include on-hover {
        background-color: var(--color-brand-violet);
      }
    }

    &.hearted,
    &.hearted.selected,
    &.hearted.preselected {
      background: var(--color-neutral-70)
        url('../../../../assets/icon/scss/map/check-white.svg')
        var(--spacing-16) center no-repeat;
      color: var(--color-white);
      padding-left: 40px;
      border: none;
      @include on-hover {
        background-color: var(--color-brand-violet);
      }
    }
    @include for-less-or-equal-than-1200-px {
      padding: 5px var(--spacing-16);

      .hearted,
      .hearted.selected,
      .hearted.preselected {
        background-size: auto 18px;
        padding-left: 34px;
      }
    }
  }
  @include for-more-or-equal-than-1200-px {
    .google-marker-container {
      @include on-hover {
        z-index: 9 !important;
      }
    }
    .google-marker-container.highlighted {
      z-index: 9 !important;
    }
  }

  @include map-zoom-control(34px, 40px, -40px, -85px);
  @include map-type-switch(var(--spacing-12));
}
</style>
